<template>
  <div class="available-subscriptions-page">
    <v-container fluid class="px-6 py-4">
      <!-- Page Header -->
      <div class="page-header mb-6">
        <div class="header-content">
          <div class="page-title-section">
            <div class="d-flex align-center mb-2">
              <h1 class="page-title">
                <v-icon large class="vibrant-title-icon mr-3">mdi-store</v-icon>
                Browse Plans
                <span class="page-inline-subtitle">Choose the perfect AI agents subscription for your needs</span>
              </h1>
              <v-spacer></v-spacer>
              <div class="header-actions">
                <v-btn 
                  outlined 
                  color="primary" 
                  to="/my-subscriptions"
                  class="modern-btn"
                >
                  <v-icon left>mdi-package-variant-closed</v-icon>
                  My Subscriptions
                </v-btn>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Pricing Toggle -->
      <div class="pricing-toggle mb-6">
        <v-card class="toggle-card" elevation="2">
          <v-card-text class="pa-4">
            <div class="toggle-content">
              <div class="toggle-text">
                <h3>Choose Your Billing Period</h3>
                <p>Save up to 20% with annual billing</p>
              </div>
              <div class="toggle-controls">
                <v-btn-toggle
                  v-model="billingPeriod"
                  color="primary"
                  group
                  dense
                  class="billing-toggle"
                >
                  <v-btn value="monthly" class="toggle-btn">
                    Monthly
                  </v-btn>
                  <v-btn value="annual" class="toggle-btn">
                    Annual
                    <v-chip color="success" dark x-small class="ml-2">Save 20%</v-chip>
                  </v-btn>
                </v-btn-toggle>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </div>

      <!-- Subscription Plans -->
      <v-row>
        <v-col
          v-for="plan in availablePlans"
          :key="plan.id"
          cols="12"
          sm="6"
          lg="4"
        >
          <v-card class="plan-card" :class="{ 'popular-plan': plan.is_popular }" elevation="8">
            <div class="plan-header" :style="`background: ${plan.color || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'}`">
              <div class="popular-badge" v-if="plan.is_popular">
                <v-icon small>mdi-star</v-icon>
                Most Popular
              </div>
              <div class="plan-name">{{ plan.name }}</div>
              <div class="plan-description">{{ plan.description }}</div>
              <div class="plan-pricing">
                <div class="price-display">
                  <span class="currency">$</span>
                  <span class="price">{{ getCurrentPrice(plan) }}</span>
                  <span class="period">/{{ billingPeriod === 'annual' ? 'year' : 'month' }}</span>
                </div>
                <div class="price-note" v-if="billingPeriod === 'annual' && plan.annual_discount">
                  <span class="original-price">${{ plan.monthly_price * 12 }}</span>
                  <span class="savings">Save ${{ (plan.monthly_price * 12) - plan.annual_price }}</span>
                </div>
              </div>
            </div>

            <v-card-text class="plan-content pa-4">
              <!-- AI Agents List -->
              <div class="agents-section mb-4">
                <h4 class="section-title">
                  <v-icon small class="mr-2">mdi-robot</v-icon>
                  AI Agents Included ({{ plan.agents?.length || 0 }})
                </h4>
                <div class="agents-list">
                  <div
                    v-for="agent in plan.agents"
                    :key="agent.id"
                    class="agent-item"
                  >
                    <v-avatar size="28" class="mr-2">
                      <v-img
                        :src="agent.logo_url || require('../../assets/sapp-only-logo.png')"
                        :alt="agent.name"
                      />
                    </v-avatar>
                    <div class="agent-info">
                      <div class="agent-name">{{ agent.name }}</div>
                      <div class="agent-value">${{ agent.monthly_price || 0 }}/mo value</div>
                    </div>
                    <v-icon color="success" small>mdi-check-circle</v-icon>
                  </div>
                </div>
              </div>

              <!-- Features List -->
              <div class="features-section" v-if="plan.features?.length">
                <h4 class="section-title">
                  <v-icon small class="mr-2">mdi-star</v-icon>
                  Plan Features
                </h4>
                <ul class="features-list">
                  <li v-for="feature in plan.features" :key="feature.id">
                    <v-icon small color="success">mdi-check</v-icon>
                    {{ feature.name }}
                  </li>
                </ul>
              </div>

              <!-- Savings Info -->
              <div class="savings-info" v-if="plan.total_savings">
                <div class="savings-card">
                  <v-icon color="success" class="mr-2">mdi-tag</v-icon>
                  <div class="savings-text">
                    <div class="savings-amount">Save ${{ plan.total_savings }}/month</div>
                    <div class="savings-note">vs individual agent pricing</div>
                  </div>
                </div>
              </div>
            </v-card-text>

            <v-card-actions class="plan-actions pa-4">
              <v-btn
                color="primary"
                block
                large
                class="subscribe-btn"
                :loading="subscribingTo === plan.id"
                @click="subscribeToPlan(plan)"
              >
                <v-icon left>mdi-plus</v-icon>
                Subscribe Now
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>

      <!-- Comparison Table -->
      <v-row class="mt-8">
        <v-col cols="12">
          <v-card class="comparison-table-card" elevation="6">
            <v-card-title class="modern-card-title">
              <h2 class="card-title">
                <v-icon class="mr-2">mdi-compare</v-icon>
                Plan Comparison
              </h2>
            </v-card-title>
            <v-card-text class="pa-0">
              <v-simple-table class="comparison-table">
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="feature-col">Features</th>
                      <th v-for="plan in availablePlans" :key="plan.id" class="plan-col">
                        {{ plan.name }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td class="feature-name">AI Agents Included</td>
                      <td v-for="plan in availablePlans" :key="plan.id" class="feature-value">
                        {{ plan.agents?.length || 0 }} agents
                      </td>
                    </tr>
                    <tr>
                      <td class="feature-name">Monthly Price</td>
                      <td v-for="plan in availablePlans" :key="plan.id" class="feature-value">
                        ${{ plan.monthly_price || 0 }}
                      </td>
                    </tr>
                    <tr>
                      <td class="feature-name">Annual Price</td>
                      <td v-for="plan in availablePlans" :key="plan.id" class="feature-value">
                        ${{ plan.annual_price || (plan.monthly_price * 12) }}
                      </td>
                    </tr>
                    <tr>
                      <td class="feature-name">Support Level</td>
                      <td v-for="plan in availablePlans" :key="plan.id" class="feature-value">
                        {{ plan.support_level || 'Standard' }}
                      </td>
                    </tr>
                    <tr>
                      <td class="feature-name">Custom Integrations</td>
                      <td v-for="plan in availablePlans" :key="plan.id" class="feature-value">
                        <v-icon :color="plan.custom_integrations ? 'success' : 'error'" small>
                          {{ plan.custom_integrations ? 'mdi-check' : 'mdi-close' }}
                        </v-icon>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- FAQ Section -->
      <v-row class="mt-8">
        <v-col cols="12">
          <v-card class="faq-card" elevation="6">
            <v-card-title class="modern-card-title">
              <h2 class="card-title">
                <v-icon class="mr-2">mdi-help-circle</v-icon>
                Frequently Asked Questions
              </h2>
            </v-card-title>
            <v-card-text class="pa-6">
              <v-expansion-panels class="faq-panels" multiple>
                <v-expansion-panel
                  v-for="(faq, index) in faqs"
                  :key="index"
                >
                  <v-expansion-panel-header class="faq-header">
                    {{ faq.question }}
                  </v-expansion-panel-header>
                  <v-expansion-panel-content class="faq-content">
                    {{ faq.answer }}
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Subscription Confirmation Dialog -->
    <v-dialog v-model="showConfirmDialog" max-width="600px" persistent>
      <v-card class="confirm-dialog" elevation="24">
        <v-card-title class="dialog-header">
          <div class="dialog-header-content">
            <div class="dialog-title-section">
              <h2 class="dialog-title">Confirm Subscription</h2>
              <p class="dialog-subtitle">{{ selectedPlan?.name }}</p>
            </div>
            <v-btn 
              icon 
              @click="showConfirmDialog = false"
              class="close-btn"
            >
              <v-icon color="white">mdi-close</v-icon>
            </v-btn>
          </div>
        </v-card-title>

        <v-card-text class="dialog-content pa-6">
          <div v-if="selectedPlan">
            <!-- Plan Summary -->
            <div class="plan-summary mb-4">
              <h3>Plan Summary</h3>
              <div class="summary-item">
                <span>Plan:</span>
                <span>{{ selectedPlan.name }}</span>
              </div>
              <div class="summary-item">
                <span>Billing Period:</span>
                <span>{{ billingPeriod === 'annual' ? 'Annual' : 'Monthly' }}</span>
              </div>
              <div class="summary-item">
                <span>Price:</span>
                <span>${{ getCurrentPrice(selectedPlan) }}/{{ billingPeriod === 'annual' ? 'year' : 'month' }}</span>
              </div>
              <div class="summary-item" v-if="billingPeriod === 'annual' && selectedPlan.annual_discount">
                <span>Annual Savings:</span>
                <span class="savings">${{ (selectedPlan.monthly_price * 12) - selectedPlan.annual_price }}</span>
              </div>
            </div>

            <!-- AI Agents Included -->
            <div class="included-agents">
              <h3>AI Agents Included</h3>
              <div class="agents-grid">
                <div
                  v-for="agent in selectedPlan.agents"
                  :key="agent.id"
                  class="agent-summary-item"
                >
                  <v-avatar size="32" class="mr-2">
                    <v-img
                      :src="agent.logo_url || require('../../assets/sapp-only-logo.png')"
                      :alt="agent.name"
                    />
                  </v-avatar>
                  <span>{{ agent.name }}</span>
                </div>
              </div>
            </div>
          </div>
        </v-card-text>

        <v-card-actions class="dialog-actions pa-6">
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="showConfirmDialog = false"
            class="cancel-btn"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            @click="confirmSubscription"
            :loading="isSubscribing"
            class="confirm-btn"
          >
            Confirm Subscription
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Notification Snackbar -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="snackbar.timeout"
      top
      right
      class="notification-snackbar"
    >
      {{ snackbar.message }}
      <template v-slot:action="{ attrs }">
        <v-btn
          text
          v-bind="attrs"
          @click="snackbar.show = false"
          color="white"
        >
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import axios from "@/axios";

export default {
  name: "AvailableSubscriptionsPage",
  data() {
    return {
      availablePlans: [],
      billingPeriod: 'monthly',
      isLoading: false,
      subscribingTo: null,
      showConfirmDialog: false,
      selectedPlan: null,
      isSubscribing: false,
      // Notification state
      snackbar: {
        show: false,
        message: '',
        color: 'success',
        timeout: 4000
      },
      faqs: [
        {
          question: "Can I change my plan later?",
          answer: "Yes, you can upgrade or downgrade your plan at any time. Changes will be prorated and reflected in your next billing cycle."
        },
        {
          question: "What happens if I cancel my subscription?",
          answer: "You can cancel anytime. Your subscription will remain active until the end of your current billing period, after which access to AI agents will be suspended."
        },
        {
          question: "Do you offer refunds?",
          answer: "We offer a 30-day money-back guarantee for new subscriptions. Contact support if you're not satisfied with our service."
        },
        {
          question: "Can I use custom integrations?",
          answer: "Custom integrations are available on Professional and Enterprise plans. Our team will help you set up custom workflows and API integrations."
        },
        {
          question: "What kind of support do you provide?",
          answer: "All plans include email support. Professional plans get priority support, and Enterprise plans include dedicated account management and phone support."
        }
      ]
    };
  },
  mounted() {
    this.fetchAvailablePlans();
  },
  methods: {
    async fetchAvailablePlans() {
      try {
        this.isLoading = true;
        const response = await axios.get('/available-subscriptions');
        // Transform the data to match expected frontend format
        this.availablePlans = (response.data.data || []).map(plan => ({
          ...plan,
          annual_price: plan.annual_price || (plan.monthly_price * 12 * 0.8), // 20% discount if not set
          annual_discount: true,
          color: plan.color || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          support_level: plan.support_level || 'Email',
          custom_integrations: plan.custom_integrations || false,
          agents: plan.agents?.map(planAgent => ({
            id: planAgent.ai_agent?.id || planAgent.id,
            name: planAgent.ai_agent?.name || planAgent.name,
            monthly_price: planAgent.ai_agent?.monthly_price || 0,
            logo_url: planAgent.ai_agent?.logo_url || null
          })) || [],
          total_savings: plan.savings || 0
        }));
      } catch (error) {
        console.error('Error fetching available plans:', error);
        this.availablePlans = [];
        // Show error message to user
        this.showErrorMessage('Failed to load subscription plans. Please try again later.');
      } finally {
        this.isLoading = false;
      }
    },
    getCurrentPrice(plan) {
      if (this.billingPeriod === 'annual') {
        return plan.annual_price || (plan.monthly_price * 12);
      }
      return plan.monthly_price || 0;
    },
    subscribeToPlan(plan) {
      this.selectedPlan = plan;
      this.showConfirmDialog = true;
    },
    async confirmSubscription() {
      try {
        this.isSubscribing = true;
        
        const payload = {
          subscription_id: this.selectedPlan.id,
          start_date: new Date().toISOString().split('T')[0], // Today's date in YYYY-MM-DD format
          is_custom: false,
          notes: `Subscribed via Browse Plans - ${this.billingPeriod} billing`
        };
        
        await axios.post('/company-subscriptions', payload);
        
        // Show success message
        this.showSuccessMessage('Subscription activated successfully!');
        
        this.showConfirmDialog = false;
        this.$router.push('/my-subscriptions');
      } catch (error) {
        console.error('Error subscribing to plan:', error);
        
        // Handle specific error cases
        if (error.response?.status === 422) {
          // Duplicate subscription or validation error
          const errorMessage = error.response.data.message || 'You already have an active subscription. Please cancel your current subscription first.';
          this.showErrorMessage(errorMessage);
        } else if (error.response?.status === 400) {
          // Bad request - company context issue
          this.showErrorMessage('Company context issue. Please refresh the page and try again.');
        } else if (error.response?.data?.errors) {
          // Validation errors
          const validationErrors = Object.values(error.response.data.errors).flat();
          this.showErrorMessage(`Validation Error: ${validationErrors.join(', ')}`);
        } else {
          // Generic error
          const errorMessage = error.response?.data?.message || 'Failed to activate subscription. Please try again.';
          this.showErrorMessage(errorMessage);
        }
        
        // Log validation errors if any for debugging
        if (error.response?.data?.errors) {
          console.error('Validation errors:', error.response.data.errors);
        }
      } finally {
        this.isSubscribing = false;
      }
    },
    showSuccessMessage(message) {
      this.snackbar = {
        show: true,
        message: message,
        color: 'success',
        timeout: 4000
      };
    },
    showErrorMessage(message) {
      this.snackbar = {
        show: true,
        message: message,
        color: 'error',
        timeout: 6000
      };
    }
  },
};
</script>

<style scoped>
.available-subscriptions-page {
  min-height: calc(100vh - 64px);
}

/* Page Header */
.page-header {
  background: white;
  border-radius: 20px;
  padding: 16px 20px;
  box-shadow: 0 10px 40px rgba(102, 126, 234, 0.1);
  border: 1px solid rgba(102, 126, 234, 0.1);
  position: relative;
  overflow: hidden;
}

.page-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.page-title {
  font-size: 24px;
  font-weight: 700;
  color: #2c3e50;
  margin: 0;
  display: flex;
  align-items: center;
}

.page-inline-subtitle {
  font-size: 16px;
  color: #64748b;
  font-weight: 400;
  margin-left: 16px;
  display: inline;
}

.page-title-section {
  flex: 1;
  width: 100%;
}

.vibrant-title-icon {
  background: linear-gradient(135deg, #ff6b6b 0%, #4ecdc4 25%, #667eea 50%, #764ba2 75%, #f093fb 100%) !important;
  -webkit-background-clip: text !important;
  background-clip: text !important;
  -webkit-text-fill-color: transparent !important;
  font-weight: 900 !important;
  filter: drop-shadow(0 2px 6px rgba(102, 126, 234, 0.4)) drop-shadow(0 0 12px rgba(255, 107, 107, 0.3));
  animation: pulse-glow 3s ease-in-out infinite;
  font-size: 32px !important;
}

@keyframes pulse-glow {
  0%, 100% { 
    filter: drop-shadow(0 2px 6px rgba(102, 126, 234, 0.4)) drop-shadow(0 0 12px rgba(255, 107, 107, 0.3));
    transform: scale(1);
  }
  50% { 
    filter: drop-shadow(0 4px 12px rgba(102, 126, 234, 0.6)) drop-shadow(0 0 20px rgba(255, 107, 107, 0.5));
    transform: scale(1.05);
  }
}

/* Pricing Toggle */
.pricing-toggle {
  display: flex;
  justify-content: center;
}

.toggle-card {
  border-radius: 16px !important;
  border: 1px solid rgba(102, 126, 234, 0.1);
  background: white;
  max-width: 600px;
  width: 100%;
}

.toggle-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
}

.toggle-text h3 {
  margin: 0 0 4px 0;
  color: #2c3e50;
  font-size: 18px;
  font-weight: 600;
}

.toggle-text p {
  margin: 0;
  color: #64748b;
  font-size: 14px;
}

.billing-toggle {
  border-radius: 12px !important;
  overflow: hidden;
  border: 1px solid #e2e8f0;
}

.toggle-btn {
  border-radius: 0 !important;
  text-transform: none !important;
  font-weight: 500 !important;
  padding: 0 20px !important;
  height: 44px !important;
}

/* Plan Cards */
.plan-card {
  border-radius: 16px !important;
  border: 1px solid rgba(102, 126, 234, 0.1);
  background: white;
  overflow: hidden;
  transition: all 0.3s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.plan-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 16px 48px rgba(102, 126, 234, 0.2) !important;
}

.popular-plan {
  border: 2px solid #10b981 !important;
  position: relative;
}

.plan-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 32px 24px;
  position: relative;
  text-align: center;
}

.popular-badge {
  position: absolute;
  top: -1px;
  left: 50%;
  transform: translateX(-50%);
  background: #10b981;
  color: white;
  padding: 6px 16px;
  border-radius: 0 0 16px 16px;
  font-size: 12px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 4px;
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3);
}

.plan-name {
  font-size: 28px;
  font-weight: 700;
  margin-bottom: 8px;
}

.plan-description {
  font-size: 16px;
  opacity: 0.9;
  margin-bottom: 24px;
}

.price-display {
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 4px;
  margin-bottom: 8px;
}

.currency {
  font-size: 24px;
  font-weight: 600;
}

.price {
  font-size: 48px;
  font-weight: 700;
}

.period {
  font-size: 18px;
  opacity: 0.8;
}

.price-note {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 14px;
}

.original-price {
  text-decoration: line-through;
  opacity: 0.7;
}

.savings {
  background: rgba(255, 255, 255, 0.2);
  padding: 2px 8px;
  border-radius: 8px;
  font-weight: 600;
}

.plan-content {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
}

.agents-list {
  max-height: 200px;
  overflow-y: auto;
}

.agent-item {
  display: flex;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f1f5f9;
}

.agent-item:last-child {
  border-bottom: none;
}

.agent-info {
  flex: 1;
  margin-left: 8px;
}

.agent-name {
  font-size: 14px;
  color: #2c3e50;
  font-weight: 600;
  margin-bottom: 2px;
}

.agent-value {
  font-size: 12px;
  color: #64748b;
}

.features-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.features-list li {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  font-size: 14px;
  color: #2c3e50;
}

.savings-info {
  margin-top: auto;
  padding-top: 16px;
}

.savings-card {
  background: rgba(16, 185, 129, 0.1);
  border: 1px solid rgba(16, 185, 129, 0.2);
  border-radius: 12px;
  padding: 12px;
  display: flex;
  align-items: center;
}

.savings-text {
  flex: 1;
}

.savings-amount {
  font-size: 16px;
  font-weight: 600;
  color: #10b981;
  margin-bottom: 2px;
}

.savings-note {
  font-size: 12px;
  color: #064e3b;
}

.plan-actions {
  background: #f8f9fa;
  border-top: 1px solid #e2e8f0;
  padding: 20px !important;
}

.subscribe-btn {
  border-radius: 12px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  letter-spacing: 0.5px !important;
  height: 48px !important;
  font-size: 16px !important;
}

/* Comparison Table */
.comparison-table-card {
  border-radius: 16px !important;
  border: 1px solid rgba(102, 126, 234, 0.1);
  background: white;
  overflow: hidden;
}

.modern-card-title {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid rgba(102, 126, 234, 0.1);
  padding: 24px !important;
}

.card-title {
  font-size: 20px;
  font-weight: 600;
  color: #2c3e50;
  margin: 0;
  display: flex;
  align-items: center;
}

.comparison-table {
  background: white;
}

.comparison-table thead th {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  color: white !important;
  font-weight: 600 !important;
  font-size: 14px !important;
  border: none !important;
  padding: 16px 12px !important;
}

.comparison-table tbody tr:hover {
  background: rgba(102, 126, 234, 0.05) !important;
}

.comparison-table tbody td {
  padding: 16px 12px !important;
  border-bottom: 1px solid rgba(102, 126, 234, 0.1) !important;
  vertical-align: middle;
}

.feature-col {
  width: 200px !important;
  min-width: 200px !important;
}

.plan-col {
  text-align: center !important;
  min-width: 120px !important;
}

.feature-name {
  font-weight: 600 !important;
  color: #2c3e50 !important;
}

.feature-value {
  text-align: center !important;
  color: #2c3e50 !important;
}

/* FAQ Section */
.faq-card {
  border-radius: 16px !important;
  border: 1px solid rgba(102, 126, 234, 0.1);
  background: white;
  overflow: hidden;
}

.faq-panels {
  box-shadow: none !important;
}

.faq-header {
  font-weight: 600 !important;
  color: #2c3e50 !important;
  font-size: 16px !important;
}

.faq-content {
  color: #64748b !important;
  line-height: 1.6 !important;
}

/* Modern Button */
.modern-btn {
  border-radius: 12px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  letter-spacing: 0.5px !important;
  padding: 0 20px !important;
  height: 44px !important;
  font-size: 14px !important;
  transition: all 0.3s ease;
  min-width: auto !important;
}

.modern-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4) !important;
}

/* Dialog Styles */
.confirm-dialog {
  border-radius: 16px !important;
  overflow: hidden;
}

.dialog-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  color: white !important;
  padding: 24px !important;
  border-radius: 0 !important;
}

.dialog-header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.dialog-title-section {
  flex: 1;
}

.dialog-title {
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  color: white;
  line-height: 1.2;
}

.dialog-subtitle {
  margin: 4px 0 0 0;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.8);
  font-weight: 400;
}

.close-btn {
  background: rgba(255, 255, 255, 0.1) !important;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.2) !important;
}

.dialog-content {
  background: #f8f9fa;
}

.plan-summary {
  background: white;
  border-radius: 12px;
  padding: 20px;
  border: 1px solid #e2e8f0;
}

.plan-summary h3 {
  margin: 0 0 16px 0;
  color: #2c3e50;
  font-size: 18px;
  font-weight: 600;
}

.summary-item {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  font-size: 14px;
}

.summary-item:last-child {
  margin-bottom: 0;
}

.summary-item .savings {
  color: #10b981;
  font-weight: 600;
}

.included-agents {
  background: white;
  border-radius: 12px;
  padding: 20px;
  border: 1px solid #e2e8f0;
}

.included-agents h3 {
  margin: 0 0 16px 0;
  color: #2c3e50;
  font-size: 18px;
  font-weight: 600;
}

.agents-grid {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.agent-summary-item {
  display: flex;
  align-items: center;
  padding: 8px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.dialog-actions {
  background: #f8f9fa;
  border-top: 1px solid rgba(102, 126, 234, 0.1);
}

.cancel-btn {
  color: #64748b !important;
  border-radius: 8px !important;
  text-transform: none !important;
  font-weight: 500 !important;
}

.cancel-btn:hover {
  background: rgba(100, 116, 139, 0.1) !important;
}

.confirm-btn {
  background: #667eea !important;
  color: white !important;
  border-radius: 8px !important;
  text-transform: none !important;
  font-weight: 600 !important;
}

.confirm-btn:hover {
  background: #5a6fd8 !important;
}

/* Notification Snackbar */
.notification-snackbar {
  z-index: 10000;
}

.notification-snackbar .v-snack__wrapper {
  border-radius: 12px !important;
  font-weight: 500;
  min-width: 300px;
}

.notification-snackbar .v-snack__content {
  padding: 16px 20px;
  font-size: 14px;
  line-height: 1.4;
}

/* Responsive Design */
@media (max-width: 960px) {
  .page-inline-subtitle {
    display: block;
    margin-left: 0;
    margin-top: 4px;
    font-size: 14px;
  }
  
  .header-content {
    flex-direction: column;
    text-align: center;
    gap: 12px;
  }
  
  .page-title {
    font-size: 22px;
  }
  
  .page-header {
    padding: 14px 18px;
  }
  
  .toggle-content {
    flex-direction: column;
    gap: 16px;
    text-align: center;
  }
  
  .plan-header {
    padding: 24px 20px;
  }
  
  .plan-name {
    font-size: 24px;
  }
  
  .price {
    font-size: 40px;
  }
}

@media (max-width: 600px) {
  .page-header {
    padding: 12px 16px;
  }
  
  .page-title {
    font-size: 20px;
  }
  
  .plan-header {
    padding: 20px 16px;
  }
  
  .plan-name {
    font-size: 22px;
  }
  
  .price {
    font-size: 36px;
  }
  
  .feature-col {
    width: 150px !important;
    min-width: 150px !important;
  }
  
  .plan-col {
    min-width: 100px !important;
  }
  
  .comparison-table tbody td,
  .comparison-table thead th {
    padding: 12px 8px !important;
    font-size: 12px !important;
  }
}
</style>